import React from "react";
import { Link } from "react-router-dom";
import { journal } from "@/data/journal";
import Reveal from "@/components/Reveal";
import SEO, { breadcrumbSchema } from "@/components/SEO";

export default function Journal() {
  const [feature, ...rest] = journal;
  return (
    <div data-testid="page-journal" className="pt-32">
      <SEO
        title="The Journal — Essays on Luxury"
        description="The Journal by Next in Luxury — essays, field notes and private conversations on luxury strategy, hospitality, retail, culture, savoir-faire and exceptional experiences."
        path="/journal"
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Journal", path: "/journal" },
        ])}
      />
      {/* Title */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-16 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-2">
          <p className="font-eyebrow opacity-60">Chapter N°05</p>
        </div>
        <div className="col-span-12 md:col-span-10">
          <Reveal>
            <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
              — Volume I, MMXXVI
            </p>
            <h1
              className="title-caps leading-[0.9] mt-8"
              style={{ fontSize: "clamp(3rem, 9vw, 9rem)" }}
            >
              THE
              <br />
              <span className="font-script" style={{ color: "#8C6B3F", textTransform: "none", letterSpacing: 0, fontSize: "1.25em" }}>
                Journal.
              </span>
            </h1>
            <p className="font-body opacity-80 mt-12 max-w-2xl text-lg leading-relaxed">
              Essays, field notes and private conversations from the discipline
              of luxury. Published irregularly, read carefully.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="hairline max-w-[1440px] mx-auto my-16" />

      {/* Feature */}
      <section
        className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-8"
        data-testid="journal-feature"
      >
        <Reveal>
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-7">
              <img
                src={feature.cover}
                alt={feature.title}
                className="w-full aspect-[4/3] object-cover duotone"
              />
            </div>
            <div className="col-span-12 md:col-span-5 flex flex-col justify-end">
              <div className="flex justify-between font-eyebrow opacity-60">
                <span>{feature.number} — Featured</span>
                <span>{feature.date}</span>
              </div>
              <p className="font-eyebrow mt-6" style={{ color: "#8C6B3F" }}>
                {feature.category}
              </p>
              <h2
                className="font-serif-display leading-[1.02] mt-6"
                style={{ fontSize: "clamp(2.25rem, 4.5vw, 4rem)" }}
              >
                {feature.title}
              </h2>
              <p className="font-body opacity-80 mt-6 text-lg">{feature.dek}</p>
              <div className="mt-8 flex justify-between font-eyebrow opacity-60">
                <span>By {feature.author}</span>
                <span>{feature.readTime}</span>
              </div>
              <Link
                to={`/journal/${feature.slug}`}
                className="btn-ghost mt-8"
                data-testid={`journal-read-${feature.slug}`}
              >
                Read the essay <span className="arrow">→</span>
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Archive */}
      <section
        className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24"
        data-testid="journal-archive"
      >
        <div className="flex justify-between items-baseline mb-16">
          <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
            The Archive
          </p>
          <p className="font-eyebrow opacity-50">Selected entries</p>
        </div>

        {rest.map((entry, i) => (
          <Reveal
            key={entry.slug}
            delay={i * 100}
            className="grid grid-cols-12 gap-6 py-12"
          >
            <div className="col-span-12 hairline mb-4" />
            <div className="col-span-2 md:col-span-1">
              <p className="font-eyebrow opacity-60">{entry.number}</p>
            </div>
            <div className="col-span-10 md:col-span-2 font-eyebrow opacity-60">
              {entry.date}
              <br />
              {entry.category}
            </div>
            <div className="col-span-12 md:col-span-6">
              <Link
                to={`/journal/${entry.slug}`}
                data-testid={`journal-entry-${entry.slug}`}
                className="block group"
              >
                <h3
                  className="font-serif-display leading-[1.05]"
                  style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
                >
                  {entry.title}
                </h3>
                <p className="font-body opacity-70 mt-4">{entry.dek}</p>
                <p className="font-eyebrow mt-4 opacity-50">
                  By {entry.author} — {entry.readTime}
                </p>
              </Link>
            </div>
            <div className="col-span-12 md:col-span-3">
              <img src={entry.cover} alt="" className="w-full aspect-[4/3] object-cover duotone" />
            </div>
          </Reveal>
        ))}
      </section>

      {/* Correspondence */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-8 md:col-start-3 text-center">
          <p className="font-eyebrow opacity-60">The Correspondence</p>
          <p
            className="font-serif-italic mt-8 leading-[1.1]"
            style={{ fontSize: "clamp(1.75rem, 3.4vw, 2.8rem)" }}
          >
            To receive the next volume of The Journal, request an introduction
            to The House.
          </p>
          <Link to="/private-inquiry" className="btn-ghost mt-12 inline-flex" data-testid="journal-inquiry-link">
            Correspond privately <span className="arrow">→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
